import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { Plus, Edit, Trash2, Copy, ExternalLink, Settings, Users, CalendarDays } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import TipoDeEventoModal from './components/TipoDeEventoModal';
import EquipeModal from './components/EquipeModal';
import ConfirmationModal from './components/ConfirmationModal';
import ConfiguracaoAgenda from './components/ConfiguracaoAgenda';
import VisitasAgenda from './components/VisitasAgenda';
import MinhaAgenda from './components/MinhaAgenda';

// --- API Functions ---
const fetchTiposDeEvento = async () => (await api.get('/agenda/tipos-de-evento')).data.data;
const fetchEmpreendimentos = async () => (await api.get('/agenda/empreendimentos')).data.data;
const createOrUpdateTipoDeEvento = async (evento) => evento.id ? (await api.put(`/agenda/tipos-de-evento/${evento.id}`, evento)).data.data : (await api.post('/agenda/tipos-de-evento', evento)).data.data;
const deleteTipoDeEvento = async (id) => (await api.delete(`/agenda/tipos-de-evento/${id}`)).data.data;

const fetchEquipes = async () => (await api.get('/agenda/equipes')).data.data;
const createOrUpdateEquipe = async (equipe) => equipe.id ? (await api.put(`/agenda/equipes/${equipe.id}`, equipe)).data.data : (await api.post('/agenda/equipes', equipe)).data.data;
const deleteEquipe = async (id) => (await api.delete(`/agenda/equipes/${id}`)).data.data;

export default function Agenda() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('minhaAgenda');

  // --- State for Modals ---
  const [isTipoDeEventoModalOpen, setIsTipoDeEventoModalOpen] = useState(false);
  const [editingTipoDeEvento, setEditingTipoDeEvento] = useState(null);
  const [isEquipeModalOpen, setIsEquipeModalOpen] = useState(false);
  const [editingEquipe, setEditingEquipe] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingType, setDeletingType] = useState('');

  // --- Queries ---
  const { data: tiposDeEvento, isLoading: isLoadingTipos, isError: isErrorTipos, error: errorTipos } = useQuery({
    queryKey: ['tiposDeEvento'],
    queryFn: fetchTiposDeEvento
  });

  const { data: empreendimentos } = useQuery({
    queryKey: ['empreendimentosList'],
    queryFn: fetchEmpreendimentos
  });

  const { data: equipes, isLoading: isLoadingEquipes, isError: isErrorEquipes, error: errorEquipes } = useQuery({
    queryKey: ['equipes'],
    queryFn: fetchEquipes
  });

  // --- Mutations ---
  const saveTipoDeEventoMutation = useMutation({
    mutationFn: createOrUpdateTipoDeEvento,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposDeEvento'] });
      setIsTipoDeEventoModalOpen(false);
      setEditingTipoDeEvento(null);
    },
  });

  const deleteTipoDeEventoMutation = useMutation({
    mutationFn: deleteTipoDeEvento,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposDeEvento'] });
      closeConfirmModal();
    },
  });

  const saveEquipeMutation = useMutation({
    mutationFn: createOrUpdateEquipe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      setIsEquipeModalOpen(false);
      setEditingEquipe(null);
    },
  });

  const deleteEquipeMutation = useMutation({
    mutationFn: deleteEquipe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      closeConfirmModal();
    },
  });

  // --- Handlers ---
  const openNewModal = () => activeTab === 'tiposDeEvento' ? openNewTipoDeEventoModal() : openNewEquipeModal();
  
  const openNewTipoDeEventoModal = () => {
    setEditingTipoDeEvento(null);
    setIsTipoDeEventoModalOpen(true);
  };

  const handleEditTipoDeEvento = (evento) => {
    setEditingTipoDeEvento(evento);
    setIsTipoDeEventoModalOpen(true);
  };
  
  const openNewEquipeModal = () => {
    setEditingEquipe(null);
    setIsEquipeModalOpen(true);
  };

  const handleEditEquipe = (equipe) => {
    setEditingEquipe(equipe);
    setIsEquipeModalOpen(true);
  };

  const handleDelete = (id, type) => {
    setDeletingId(id);
    setDeletingType(type);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = () => {
    if (deletingType === 'tipoDeEvento') {
      deleteTipoDeEventoMutation.mutate(deletingId);
    } else if (deletingType === 'equipe') {
      deleteEquipeMutation.mutate(deletingId);
    }
  };

  const closeConfirmModal = () => {
    setIsConfirmModalOpen(false);
    setDeletingId(null);
    setDeletingType('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Link copiado para a área de transferência!');
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="text-primary" />
            Gestão de Agenda
          </h1>
          <p className="text-gray-500 text-sm">Configure e gerencie agendamentos e equipes integrados ao Cal.com</p>
        </div>
        {activeTab !== 'config' && activeTab !== 'visitas' && activeTab !== 'minhaAgenda' && (
          <button 
            onClick={openNewModal} 
            className="inline-flex items-center justify-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
          >
            <Plus size={18} className="mr-2" />
            {activeTab === 'tiposDeEvento' ? 'Novo Tipo de Evento' : 'Nova Equipe'}
          </button>
        )}
      </div>

      <div className="flex border-b border-gray-200 gap-4 overflow-x-auto no-scrollbar">
        <button
          onClick={() => setActiveTab('minhaAgenda')}
          className={`pb-4 px-2 text-sm font-medium transition-all whitespace-nowrap border-b-2 ${activeTab === 'minhaAgenda' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'} flex items-center gap-2`}
        >
          <CalendarDays size={16} />
          Minha Agenda
        </button>
        <button
          onClick={() => setActiveTab('tiposDeEvento')}
          className={`pb-4 px-2 text-sm font-medium transition-all whitespace-nowrap border-b-2 ${activeTab === 'tiposDeEvento' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Tipos de Evento
        </button>
        <button 
          onClick={() => setActiveTab('equipes')} 
          className={`pb-4 px-2 text-sm font-medium transition-all whitespace-nowrap border-b-2 ${activeTab === 'equipes' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Equipes
        </button>
        <button 
          onClick={() => setActiveTab('config')} 
          className={`pb-4 px-2 text-sm font-medium transition-all whitespace-nowrap border-b-2 ${activeTab === 'config' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'} flex items-center gap-2`}
        >
          <Settings size={16} />
          Configurações
        </button>
        <button
          onClick={() => setActiveTab('visitas')} 
          className={`pb-4 px-2 text-sm font-medium transition-all whitespace-nowrap border-b-2 ${activeTab === 'visitas' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'} flex items-center gap-2`}
        >
          <CalendarDays size={16} />
          Visitas
        </button>
      </div>

      <div className="mt-2">
        {activeTab === 'minhaAgenda' && (
          <MinhaAgenda />
        )}

        {activeTab === 'tiposDeEvento' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {isLoadingTipos && <div className="p-8 text-center text-gray-400">Carregando eventos...</div>}
            {isErrorTipos && <div className="p-8 text-center text-red-500 font-medium">Erro ao carregar: {errorTipos.message}</div>}
            {tiposDeEvento && (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase">Evento</th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase">Empreendimento</th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase text-center">Duração</th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase">Links</th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase text-center">Status</th>
                      <th className="text-right py-3 px-6 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tiposDeEvento.map((tipo) => (
                      <tr key={tipo.id} className="hover:bg-gray-50 transition-colors group">
                        <td className="py-4 px-6">
                          <div className="font-semibold text-gray-900">{tipo.nome}</div>
                          <div className="text-xs text-gray-500">/{tipo.slug}</div>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600 font-medium">
                          {empreendimentos?.find(e => e.id === tipo.empreendimento_id)?.nome || <span className="text-gray-400 font-normal">Não vinculado</span>}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600 text-center">{tipo.duracao} min</td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            {tipo.cal_link ? (
                              <>
                                <button 
                                  onClick={() => copyToClipboard(tipo.cal_link)} 
                                  title="Copiar Link" 
                                  className="p-1.5 text-primary hover:bg-blue-50 rounded-md transition-colors"
                                >
                                  <Copy size={16} />
                                </button>
                                <a 
                                  href={tipo.cal_link} 
                                  target="_blank" 
                                  rel="noreferrer" 
                                  title="Abrir Link" 
                                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                                >
                                  <ExternalLink size={16} />
                                </a>
                              </>
                            ) : (
                              <span className="text-xs text-gray-300 italic">Sem link</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tipo.ativo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {tipo.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => handleEditTipoDeEvento(tipo)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
                              <Edit size={16} />
                            </button>
                            <button onClick={() => handleDelete(tipo.id, 'tipoDeEvento')} className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {tiposDeEvento.length === 0 && (
                      <tr>
                        <td colSpan="6" className="p-12 text-center text-gray-400 text-sm italic">Nenhum tipo de evento cadastrado ainda.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'equipes' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {isLoadingEquipes && <div className="p-8 text-center text-gray-400">Carregando equipes...</div>}
            {isErrorEquipes && <div className="p-8 text-center text-red-500 font-medium">Erro ao carregar: {errorEquipes.message}</div>}
            {equipes && (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase">Nome da Equipe</th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase text-center">Status</th>
                      <th className="text-right py-3 px-6 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {equipes.map((equipe) => (
                      <tr key={equipe.id} className="hover:bg-gray-50 transition-colors group">
                        <td className="py-4 px-6">
                          <RouterLink 
                            to={`/agenda/equipes/${equipe.id}`} 
                            className="font-semibold text-primary hover:text-secondary flex items-center gap-2"
                          >
                            <Users size={16} />
                            {equipe.nome}
                          </RouterLink>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${equipe.ativo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {equipe.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => handleEditEquipe(equipe)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
                              <Edit size={16} />
                            </button>
                            <button onClick={() => handleDelete(equipe.id, 'equipe')} className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {equipes.length === 0 && (
                      <tr>
                        <td colSpan="3" className="p-12 text-center text-gray-400 text-sm italic">Nenhuma equipe cadastrada ainda.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'config' && (
          <ConfiguracaoAgenda />
        )}

        {activeTab === 'visitas' && (
          <VisitasAgenda />
        )}
      </div>

      <TipoDeEventoModal 
        isOpen={isTipoDeEventoModalOpen} 
        onClose={() => setIsTipoDeEventoModalOpen(false)} 
        onSave={(data) => saveTipoDeEventoMutation.mutate(data)} 
        evento={editingTipoDeEvento} 
      />
      <EquipeModal 
        isOpen={isEquipeModalOpen} 
        onClose={() => setIsEquipeModalOpen(false)} 
        onSave={(data) => saveEquipeMutation.mutate(data)} 
        equipe={editingEquipe} 
      />
      <ConfirmationModal 
        isOpen={isConfirmModalOpen} 
        onClose={closeConfirmModal} 
        onConfirm={confirmDelete} 
        title="Confirmar Exclusão" 
        message={`Tem certeza que deseja excluir est${deletingType === 'equipe' ? 'a' : 'e'} ${deletingType === 'equipe' ? 'equipe' : 'tipo de evento'}?`} 
      />
    </div>
  );
}
